import { readDesktopPrimaryBearer } from "./desktopAuth";

async function send(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  fetchFn: typeof globalThis.fetch,
  bearer: string | null,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (bearer !== null) {
    headers.set("authorization", `Bearer ${bearer}`);
  }
  return fetchFn(input, {
    ...init,
    headers,
    // The Desktop Client's bearer is the whole authority. A browser served by the Environment
    // carries its paired session as an HttpOnly cookie instead, which only `include` sends.
    credentials: bearer === null ? "include" : "omit",
  });
}

/**
 * How this client authorizes an HTTP request to its primary Environment.
 *
 * Every protected route on the Environment Service — `/api/`, the Environment Access routes, the
 * descriptor — is answered only to a paired caller. The Desktop Client presents the bearer session
 * its main process paired for it, because the renderer is served from the `afar-research://`
 * scheme and a cookie for the Environment's origin is not something it can hold. A browser is the
 * other way round: it is served by the Environment and carries its session cookie.
 *
 * Anything in the renderer that reaches the primary Environment over HTTP goes through here, so
 * that choice is made once.
 */
export async function primaryEnvironmentFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  fetchFn: typeof globalThis.fetch = globalThis.fetch,
): Promise<Response> {
  const bearer = await readDesktopPrimaryBearer();
  const response = await send(input, init, fetchFn, bearer);
  if (bearer === null || response.status !== 401) return response;
  // Refused: the Environment no longer knows this session. Pair once more and retry once.
  return send(input, init, fetchFn, await readDesktopPrimaryBearer({ refresh: true }));
}
